import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import {
  MatrixAccess,
  RacMatrix,
  RacPage,
  RacRole,
  RolesAccessService,
} from './roles-access.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';

interface ModuleGroup {
  module: string;
  pages: RacPage[];
}

/**
 * Roles & Access Control admin page: per-role page/tab access matrix
 * (None / View / Full) plus per-page Warehouse/Store availability.
 * SUPER_ADMIN and Admin bypass the matrix entirely (enforced server-side).
 */
@Component({
  selector: 'app-roles-access',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './roles-access.component.html',
})
export class RolesAccessComponent implements OnInit {
  private service = inject(RolesAccessService);
  private toastr = inject(ToastrService);
  private confirmDialog = inject(ConfirmDialogService);

  loading = true;
  saving = false;
  matrix: RacMatrix | null = null;
  moduleGroups: ModuleGroup[] = [];

  /** Roles that can actually be configured (superusers are fixed at FULL). */
  editableRoles: RacRole[] = [];
  superUserNames = '';
  selectedRoleId: string | null = null;
  /** Role the current draft was built from — used to revert a declined switch. */
  private appliedRoleId: string | null = null;
  private appliedRoleName = '';

  /** pageKey -> access for the selected role; the editable draft. */
  draft = new Map<string, MatrixAccess>();
  dirty = false;

  /** Page keys with an in-flight location-type PATCH. */
  savingLocation = new Set<string>();

  newRoleName = '';
  creatingRole = false;

  readonly accessLevels: MatrixAccess[] = ['NONE', 'VIEW', 'FULL'];

  ngOnInit(): void {
    this.loadMatrix();
  }

  private loadMatrix(selectRoleId?: string): void {
    this.loading = true;
    this.service.getMatrix().subscribe({
      next: (matrix) => {
        this.matrix = matrix;
        this.editableRoles = matrix.roles.filter((r) => !r.isSuperUser);
        this.superUserNames = matrix.roles
          .filter((r) => r.isSuperUser)
          .map((r) => r.name)
          .join(', ');
        this.groupPages(matrix.pages);
        this.selectedRoleId =
          selectRoleId ??
          this.selectedRoleId ??
          this.editableRoles[0]?.id ??
          null;
        this.buildDraft();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Failed to load access matrix');
      },
    });
  }

  private groupPages(pages: RacPage[]): void {
    const groups: ModuleGroup[] = [];
    for (const page of pages) {
      let group = groups.find((g) => g.module === page.module);
      if (!group) {
        group = { module: page.module, pages: [] };
        groups.push(group);
      }
      group.pages.push(page);
    }
    this.moduleGroups = groups;
  }

  async onRoleChange(): Promise<void> {
    // ngModel has already moved selectedRoleId to the new role by the time this
    // fires, so a declined confirm has to put the dropdown back.
    if (this.dirty && this.selectedRoleId !== this.appliedRoleId) {
      const discard = await this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved access changes for <strong>' +
          (this.appliedRoleName || 'the previous role') +
          '</strong>. Switching roles will discard them.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        size: 'lg',
      });
      if (!discard) {
        this.selectedRoleId = this.appliedRoleId;
        return;
      }
    }
    this.buildDraft();
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.dirty) return true;
    return this.confirmDialog.confirm({
      title: 'Discard changes?',
      message:
        'You have unsaved changes in <strong>Roles &amp; Access</strong>. Are you sure you want to leave this page?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
  }

  private buildDraft(): void {
    this.draft.clear();
    this.dirty = false;
    this.appliedRoleId = this.selectedRoleId;
    this.appliedRoleName = this.selectedRole?.name ?? '';
    if (!this.matrix || !this.selectedRoleId) return;
    const pageById = new Map(this.matrix.pages.map((p) => [p.id, p]));
    for (const page of this.matrix.pages) this.draft.set(page.key, 'NONE');
    for (const grant of this.matrix.grants) {
      if (grant.roleId !== this.selectedRoleId) continue;
      const page = pageById.get(grant.pageId);
      if (page) this.draft.set(page.key, grant.access);
    }
  }

  getAccess(pageKey: string): MatrixAccess {
    return this.draft.get(pageKey) ?? 'NONE';
  }

  setAccess(pageKey: string, level: MatrixAccess): void {
    if (this.getAccess(pageKey) === level) return;
    this.draft.set(pageKey, level);
    this.dirty = true;
  }

  setAllInModule(group: ModuleGroup, level: MatrixAccess): void {
    for (const page of group.pages) this.draft.set(page.key, level);
    this.dirty = true;
  }

  save(): void {
    if (!this.selectedRoleId || !this.matrix) return;
    this.saving = true;
    const entries = this.matrix.pages.map((page) => ({
      pageKey: page.key,
      access: this.getAccess(page.key),
    }));
    this.service.saveMatrix(this.selectedRoleId, entries).subscribe({
      next: () => {
        this.saving = false;
        this.dirty = false;
        // Refresh grants locally so switching roles shows the saved state.
        this.loadMatrix(this.selectedRoleId!);
        this.toastr.success('Access updated. Users see changes on next page load.');
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Failed to save access changes');
      },
    });
  }

  /** Checked = page available at that location type ([] = everywhere). */
  isLocationAllowed(page: RacPage, type: 'Warehouse' | 'Store'): boolean {
    return (
      page.allowedLocationTypes.length === 0 ||
      page.allowedLocationTypes.includes(type)
    );
  }

  toggleLocationType(page: RacPage, type: 'Warehouse' | 'Store'): void {
    const other: 'Warehouse' | 'Store' =
      type === 'Warehouse' ? 'Store' : 'Warehouse';
    const currentlyAllowed = this.isLocationAllowed(page, type);

    if (currentlyAllowed && !this.isLocationAllowed(page, other)) {
      this.toastr.warning('A page must stay available at least one location type');
      return;
    }
    // Unchecking one leaves only the other; re-checking both clears the
    // restriction entirely (empty = available everywhere).
    const next = currentlyAllowed ? [other] : [];

    this.savingLocation.add(page.key);
    this.service.updateLocationTypes(page.key, next).subscribe({
      next: (res) => {
        page.allowedLocationTypes = res.allowedLocationTypes;
        this.savingLocation.delete(page.key);
      },
      error: () => {
        this.savingLocation.delete(page.key);
        this.toastr.error('Failed to update page availability');
      },
    });
  }

  createRole(): void {
    const name = this.newRoleName.trim();
    if (!name) return;
    this.creatingRole = true;
    this.service.createRole(name).subscribe({
      next: (role) => {
        this.creatingRole = false;
        this.newRoleName = '';
        this.toastr.success(`Role "${role.name}" created`);
        this.loadMatrix(role.id);
      },
      error: (err) => {
        this.creatingRole = false;
        this.toastr.error(
          err?.error?.message || 'Failed to create role'
        );
      },
    });
  }

  get selectedRole(): RacRole | undefined {
    return this.editableRoles.find((r) => r.id === this.selectedRoleId);
  }
}
